import { useEffect, useMemo, useState } from "react";
import { submitForm, validateAddress } from "./api";
import { US_STATES } from "./states";

type FormState = {
  first_name: string;
  last_name: string;
  phone: string;
  address: {
    line1: string;
    line2: string;
    city: string;
    state: string;
    zip: string;
  };
  program_type: string;
};

const initialForm: FormState = {
  first_name: "",
  last_name: "",
  phone: "",
  address: { line1: "", line2: "", city: "", state: "", zip: "" },
  program_type: ""
};

type ValidationErrors = Record<string, string>;

function App() {
  const [form, setForm] = useState<FormState>(initialForm);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [addressValidated, setAddressValidated] = useState(false);
  const [addressMessage, setAddressMessage] = useState<string>("");
  const [statusMessage, setStatusMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [validating, setValidating] = useState(false);

  useEffect(() => {
    setAddressValidated(false);
    setAddressMessage("");
  }, [form.address.line1, form.address.line2, form.address.city, form.address.state, form.address.zip]);

  const validationErrors = useMemo(() => errors, [errors]);

  const handleChange = (field: string, value: string) => {
    if (field.startsWith("address.")) {
      const key = field.replace("address.", "") as keyof FormState["address"];
      setForm((prev) => ({ ...prev, address: { ...prev.address, [key]: value } }));
    } else {
      setForm((prev) => ({ ...prev, [field]: value }));
    }
  };

  const localValidate = () => {
    const newErrors: ValidationErrors = {};
    if (!form.first_name.trim()) newErrors.first_name = "First name is required.";
    if (!form.last_name.trim()) newErrors.last_name = "Last name is required.";
    const digits = form.phone.replace(/\D/g, "");
    if (digits.length !== 10) newErrors.phone = "Enter a 10-digit U.S. phone number.";
    if (!form.address.line1.trim()) newErrors["address.line1"] = "Address line 1 is required.";
    if (!form.address.city.trim()) newErrors["address.city"] = "City is required.";
    if (!form.address.state.trim()) newErrors["address.state"] = "State is required.";
    if (!/^[0-9]{5}$/.test(form.address.zip)) newErrors["address.zip"] = "Enter a 5-digit ZIP.";
    if (!form.program_type) newErrors.program_type = "Select a program.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateAddressOnly = () => {
    const newErrors: ValidationErrors = { ...errors };
    let isValid = true;

    if (!form.address.line1.trim()) {
      newErrors["address.line1"] = "Address line 1 is required.";
      isValid = false;
    } else {
      delete newErrors["address.line1"];
    }
    if (!form.address.city.trim()) {
      newErrors["address.city"] = "City is required.";
      isValid = false;
    } else {
      delete newErrors["address.city"];
    }
    if (!form.address.state.trim()) {
      newErrors["address.state"] = "State is required.";
      isValid = false;
    } else {
      delete newErrors["address.state"];
    }
    if (!/^[0-9]{5}$/.test(form.address.zip)) {
      newErrors["address.zip"] = "Enter a 5-digit ZIP.";
      isValid = false;
    } else {
      delete newErrors["address.zip"];
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleValidateAddress = async () => {
    if (!validateAddressOnly()) {
      setAddressValidated(false);
      setAddressMessage("");
      return;
    }
    setValidating(true);
    setAddressMessage("");
    try {
      const res = await validateAddress(form.address);
      if (res.isValid) {
        setAddressValidated(true);
        setAddressMessage("Address validated.");
      } else {
        setAddressValidated(false);
        setAddressMessage(res.message || "Address could not be validated. Please review.");
      }
    } catch (err: any) {
      setAddressValidated(false);
      setAddressMessage(err.message || "Address validation failed.");
    } finally {
      setValidating(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatusMessage("");
    if (!localValidate()) return;
    if (!addressValidated) {
      setAddressMessage("Please validate your address before submitting.");
      return;
    }
    setSubmitting(true);
    try {
      await submitForm({ ...form, client_metadata: {} });
      setStatusMessage("Request submitted. Your forms will be mailed soon.");
      setForm(initialForm);
      setAddressValidated(false);
      setErrors({});
    } catch (err: any) {
      setStatusMessage(err.message || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="page">
      <header className="hero">
        <div>
          <p className="eyebrow">Missouri Department of Social Services</p>
          <h1>Request Paper Application Forms</h1>
          <p className="lede">
            Tell us where to send your SNAP Application Form. We will mail it to the address you
            provide after validation.
          </p>
        </div>
      </header>

      <main className="card">
        <form onSubmit={handleSubmit} noValidate>
          <section className="section">
            <h2>Contact information</h2>
            <div className="grid two">
              <Field
                label="First name"
                id="first_name"
                value={form.first_name}
                error={validationErrors.first_name}
                onChange={(v) => handleChange("first_name", v)}
                required
              />
              <Field
                label="Last name"
                id="last_name"
                value={form.last_name}
                error={validationErrors.last_name}
                onChange={(v) => handleChange("last_name", v)}
                required
              />
            </div>
            <Field
              label="Phone number"
              id="phone"
              type="tel"
              placeholder="555-123-4567"
              value={form.phone}
              error={validationErrors.phone}
              onChange={(v) => handleChange("phone", v)}
              required
              hint="We may contact you if we need more information."
            />
          </section>

          <section className="section">
            <h2>Mailing address</h2>
            <p className="hint">We must validate this address before sending your forms.</p>
            <Field
              label="Address line 1"
              id="line1"
              value={form.address.line1}
              error={validationErrors["address.line1"]}
              onChange={(v) => handleChange("address.line1", v)}
              required
            />
            <Field
              label="Address line 2 (optional)"
              id="line2"
              value={form.address.line2}
              error={validationErrors["address.line2"]}
              onChange={(v) => handleChange("address.line2", v)}
            />
            <div className="grid three">
              <Field
                label="City"
                id="city"
                value={form.address.city}
                error={validationErrors["address.city"]}
                onChange={(v) => handleChange("address.city", v)}
                required
              />
              <FieldSelect
                label="State"
                id="state"
                value={form.address.state}
                options={US_STATES}
                error={validationErrors["address.state"]}
                onChange={(v) => handleChange("address.state", v)}
                required
              />
              <Field
                label="ZIP"
                id="zip"
                value={form.address.zip}
                error={validationErrors["address.zip"]}
                onChange={(v) => handleChange("address.zip", v)}
                required
                maxLength={5}
              />
            </div>
            {addressMessage && (
              <div className={`status ${addressValidated ? "success" : "warning"}`} role="status">
                {addressMessage}
              </div>
            )}
          </section>

          <section className="section">
            <h2>Program selection</h2>
            <FieldSelect
              label="Program type"
              id="program_type"
              value={form.program_type}
              options={[
                { value: "SNAP", label: "SNAP" }
              ]}
              error={validationErrors.program_type}
              onChange={(v) => handleChange("program_type", v)}
              required
            />
          </section>

          <div className="actions steps">
            <div className="step-action">
              <p className="step-label">Step 1</p>
              <button type="button" className="secondary" onClick={handleValidateAddress} disabled={validating}>
                {validating ? "Validating..." : "Validate address"}
              </button>
            </div>
            <div className="step-action">
              <p className="step-label">Step 2</p>
              <button type="submit" disabled={!addressValidated || submitting}>
                {submitting ? "Submitting..." : "Submit request"}
              </button>
            </div>
          </div>
          {statusMessage && (
            <div className="status info" role="status">
              {statusMessage}
            </div>
          )}
          <p className="footnote">
            Need help? Visit <a href="https://dss.mo.gov/" target="_blank" rel="noreferrer">dss.mo.gov</a>.
          </p>
        </form>
      </main>
    </div>
  );
}

type FieldProps = {
  label: string;
  id: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  error?: string;
  required?: boolean;
  maxLength?: number;
  hint?: string;
};

function Field({
  label,
  id,
  value,
  onChange,
  type = "text",
  placeholder,
  error,
  required,
  maxLength,
  hint
}: FieldProps) {
  return (
    <label className="field" htmlFor={id}>
      <div className="label-row">
        <span className="label-text">{label}</span>
        {required && <span className="required">Required</span>}
      </div>
      <input
        id={id}
        name={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-invalid={!!error}
        aria-describedby={error ? `${id}-error` : undefined}
        maxLength={maxLength}
      />
      {hint && <p className="hint">{hint}</p>}
      {error && (
        <p className="error" id={`${id}-error`} role="alert">
          {error}
        </p>
      )}
    </label>
  );
}

type FieldSelectProps = {
  label: string;
  id: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  error?: string;
  required?: boolean;
};

function FieldSelect({ label, id, value, onChange, options, error, required }: FieldSelectProps) {
  return (
    <label className="field" htmlFor={id}>
      <div className="label-row">
        <span className="label-text">{label}</span>
        {required && <span className="required">Required</span>}
      </div>
      <select
        id={id}
        name={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-invalid={!!error}
        aria-describedby={error ? `${id}-error` : undefined}
      >
        <option value="">Select...</option>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
      {error && (
        <p className="error" id={`${id}-error`} role="alert">
          {error}
        </p>
      )}
    </label>
  );
}

export default App;
